import type { ReactNode } from 'react'

import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'

import { ContentBriefBlockEditor } from './content-brief-block-editor.atom'
import { useContentBriefBlockKeyboard } from './use-content-brief-block-keyboard.hook'
import { useContentBriefShadowPopoverState } from './use-content-brief-shadow-popover-state.hook'

export interface ContentBriefShadowCommentPopoverProps {
  blockIndex: number | null
  blockRect: DOMRect | null
  comment: string | undefined
  onCommentChange: (blockIndex: number, comment: string | undefined) => void
  onClose: () => void
}

const buildAnchorStyle = (rect: DOMRect): React.CSSProperties => ({
  position: 'fixed',
  top: rect.top,
  left: rect.left,
  width: rect.width,
  height: rect.height,
  pointerEvents: 'none',
})

export const ContentBriefShadowCommentPopover: React.FC<ContentBriefShadowCommentPopoverProps> = ({
  blockIndex,
  blockRect,
  comment,
  onCommentChange,
  onClose,
}): ReactNode => {
  const isOpen = blockIndex !== null && blockRect !== null
  const { draftComment, setDraftComment, textareaRef, handleSave, handleCancel, handleDelete } =
    useContentBriefShadowPopoverState({
      blockIndex,
      comment,
      isOpen,
      onCommentChange,
      onClose,
    })
  const { handleKeyDown } = useContentBriefBlockKeyboard({
    isEditing: isOpen,
    handleBlockClick: () => undefined,
    handleSave,
    handleCancel,
  })

  return (
    <Popover open={isOpen} onOpenChange={(open) => !open && onClose()}>
      {blockRect && (
        <PopoverAnchor asChild>
          <div className="content-brief-shadow-anchor" style={buildAnchorStyle(blockRect)} />
        </PopoverAnchor>
      )}
      <PopoverContent
        className="content-brief-shadow-popover w-96 p-0"
        side="right"
        align="start"
        sideOffset={12}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <ContentBriefBlockEditor
          draftComment={draftComment}
          hasComment={Boolean(comment)}
          textareaRef={textareaRef}
          onDraftChange={setDraftComment}
          onKeyDown={handleKeyDown}
          onSave={handleSave}
          onCancel={handleCancel}
          onDelete={handleDelete}
        />
      </PopoverContent>
    </Popover>
  )
}
